import React, { useEffect, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Form } from 'react-bootstrap'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Container from 'react-bootstrap/Container'
import './WorkflowFormContainer.scss'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { appRoutes } from '../../utils/routes'
import { ArrowLeftShort } from 'react-bootstrap-icons'
import { addWorkflow, createWorkFlowQR, getWorkFlowById, shipWorkflow, updateWorkflow } from '../../services/Workflow'
import { AxiosResponse } from 'axios'
import { trackPromise } from 'react-promise-tracker'
import { AppUtil } from '../../utils/app.utils'
import { WorkFlowDefaultData, WorkFlowFormSchema } from './helpers'
import { QrCodeDetails, Workflow, WorkflowRequestPayload } from '../../models/Workflow'
import { CreateWorkflow } from './components'
import { formatWorkflowPayloadData } from './helpers/AddWorkflowHelpers'
import { WorkflowQRCodes } from './WorkflowQRCodes'
import * as _ from 'lodash'
import { SampleStatusMasterData } from '../SampleInformationForm/helpers'

const shippedOrReportReady = [
  SampleStatusMasterData.sampleStatusValueMap.ready_for_shipment,
  SampleStatusMasterData.sampleStatusValueMap.shipped,
  SampleStatusMasterData.sampleStatusValueMap.report_ready,
];

export const WorkflowFormContainer = () => {
  const { patientId, id } = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const translatn = 'workflowContainer.'
  const parentClass = 'vcr-pp-workflow-'

  const [isCallFetchAPI, setIsCallFetchAPI] = useState<boolean>(true)
  const [qrCodeDetails, setQRCodeDetails] = useState<QrCodeDetails>({} as QrCodeDetails)

  let formDefaultValues = JSON.parse(JSON.stringify(WorkFlowDefaultData))
  formDefaultValues.patientId = patientId
  formDefaultValues.id = id ?? ''

  const formMethods = useForm<Workflow>({
    resolver: zodResolver(WorkFlowFormSchema),
    mode: 'all',
    defaultValues: formDefaultValues,
  })

  const handleCreateQRCode = async () => {
    await createWorkFlowQR(id ?? '').then((response: AxiosResponse<QrCodeDetails>) => {
      if (response.status == 200) {
        setQRCodeDetails(response.data)
        formMethods.setValue('draftSamples', response.data?.draftSamples)
        formMethods.setValue('samples', response.data?.confirmedSamples)
        formMethods.setValue('status', response.data.status)
        formMethods.setValue('removedSamples', '')
      }
    }, (error: any) => {
      AppUtil.logError(error)
    })
  }

  const getWorkflowByIdData = () => {
    getWorkFlowById(id ?? '').then((response: AxiosResponse<WorkflowRequestPayload>) => {
      if (response.status == 200) {
        if (response.data?.status === SampleStatusMasterData.sampleStatusValueMap.report_ready) {
          navigate(generatePath(appRoutes.viewPatientDetails, { id: patientId }) + "#workflow")
        } else {
          setIsCallFetchAPI(true)
          formMethods.reset({ ...response.data })
          formMethods.setValue('sampleIDs', [])
          formMethods.setValue('workflowName', response.data?.workFlowId)
          formMethods.setValue('draftSamples', response.data?.draftSamples)
          formMethods.setValue('samples', response.data?.samples)
          formMethods.setValue('status', response.data?.status)
          formMethods.setValue('removedSamples', '')
          if (shippedOrReportReady.includes(response.data?.status)) {
            handleCreateQRCode()
          }
        }
      }
    }, (error: any) => {
      AppUtil.logError(error)
    })
  }

  useEffect(() => {
    if (id) {
      getWorkflowByIdData()
    }
  }, [id])

  const saveWorkflow = async function () {
    const data: WorkflowRequestPayload = formatWorkflowPayloadData(formMethods.getValues())
    if (!id) {
      await trackPromise(
        addWorkflow(data).then(
          (res: AxiosResponse<{ WorkFlowDetails: WorkflowRequestPayload }>) => {
            if (res.status == 200) {
              navigate(generatePath(appRoutes.editWorkflow, { patientId: patientId, id: res.data.WorkFlowDetails?.id }))
            }
          },
          (error) => AppUtil.logError(error),
        ),
      )
    } else {
      updateWorkflow(data).then((response: AxiosResponse<{ WorkFlowDetails: WorkflowRequestPayload }>) => {
        if (response.status == 200) {
          getWorkflowByIdData()
        }
      }, (error) =>
        AppUtil.logError(error)
      );
    }
  }

  const handleShip = () => {
    shipWorkflow(id ?? '').then(
      (res: AxiosResponse<{ WorkFlowDetails: WorkflowRequestPayload }>) => {
        if (res.status == 200) {
          formMethods.reset({ ...res.data.WorkFlowDetails })
          formMethods.setValue('sampleIDs', [])
          formMethods.setValue('workflowName', res.data?.WorkFlowDetails?.workFlowId)
          formMethods.setValue('draftSamples', res.data?.WorkFlowDetails?.draftSamples)
          formMethods.setValue('samples', res.data?.WorkFlowDetails?.samples)
          formMethods.setValue('status', res.data?.WorkFlowDetails?.status)
        }
      },
      (error) => AppUtil.logError(error),
    )
  }

  return (
    <Container className={`${parentClass}container`} fluid>
      <Row className={`${parentClass}header `}>
        <Col className={`${parentClass}header-left-content`}>
          <ArrowLeftShort size={36} onClick={() => navigate(generatePath(appRoutes.viewPatientDetails, { id: patientId }) + "#workflow")} />
          <h4 className={`${parentClass}header-text `}>
            {id ? t(`${translatn}edit`) : t(`${translatn}new`)}
          </h4>
        </Col>
      </Row>
      <Row className={`${parentClass}form`}>
        <Col lg={12} md={12} sm={12} /* style={{ paddingRight: 0 }} */>
          <FormProvider {...formMethods}>
            <Form noValidate>
              <CreateWorkflow isCallFetchAPI={isCallFetchAPI} setIsCallFetchAPI={setIsCallFetchAPI} />
            </Form>
          </FormProvider>
        </Col>
      </Row>
      <Row className={`${parentClass}save`}>
        <Col className="d-flex justify-content-end">
          {(id && !formMethods.watch('sampleIDs')?.length && formMethods.watch('status') === SampleStatusMasterData.sampleStatusValueMap.ready_for_shipment && !formMethods.watch('removedSamples') ? <Button className={`${parentClass}save-btn`} onClick={handleShip} >
            {t(`${translatn}ship`)}
          </Button> : (id && !formMethods.watch('sampleIDs')?.length && formMethods.watch('status') === SampleStatusMasterData.sampleStatusValueMap.draft && !formMethods.watch('removedSamples') ? <Button className={`${parentClass}save-btn`} onClick={handleCreateQRCode}>
            {t(`${translatn}createQRCode`)}
          </Button> : (formMethods.watch('sampleIDs')?.length || !!formMethods.watch('removedSamples') ? <Button className={`${parentClass}save-btn`} type="submit" onClick={formMethods.handleSubmit(saveWorkflow)}>
            {id ? t(`${translatn}update`) : t(`${translatn}save`)}{' '}
          </Button> : <></>)))}
        </Col>
        {!_.isEmpty(qrCodeDetails) && (<div className={'mt-4'}>
          <WorkflowQRCodes qrCodeDetails={qrCodeDetails} />
        </div>
        )}
      </Row>
    </Container>
  )
}
